import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
} from "@angular/core";
import { TreeSelect } from "primeng/treeselect";

@Component({
  selector: "app-key-account-tree-select",
  templateUrl: "./key-account-tree-select.component.html",
  styleUrl: "./key-account-tree-select.component.scss",
})
export class KeyAccountTreeSelectComponent implements OnInit, OnChanges {
  @Input() treeNodeKeyAccountList: any[] = [];
  @Input() footerButtonShow: boolean = false; // New input to control footer button visibility
  @Input() selectionMode: "single" | "checkbox" = "checkbox"; // Selection mode for tree select
  @Input() disableCheckboxSelection: boolean = false;
  @Input() disabled: boolean = false;
  @Output() onSelectedKeyAccountsEmit = new EventEmitter<any>();
  @ViewChild(TreeSelect) treeSelect!: TreeSelect;

  selectedNodeKeyAccount: any[] = [];
  selectAll: boolean = false;

  // Custom filter properties
  searchValue: string = "";
  filteredTreeNodeList: any[] = [];

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.filteredTreeNodeList = [...this.treeNodeKeyAccountList];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["treeNodeKeyAccountList"] &&
      !this.selectedNodeKeyAccount?.length
    ) {
      this.filteredTreeNodeList = [...this.treeNodeKeyAccountList];
      this.applyFilter();
    }
  }

  // Method to handle custom filter input changes
  onCustomFilterChange(searchText: string): void {
    this.searchValue = searchText;
    this.applyFilter();
  }

  private applyFilter(): void {
    const value = this.searchValue?.trim().toLowerCase();

    if (!value) {
      this.filteredTreeNodeList = this.treeNodeKeyAccountList;
      this.updateTreeSelection();
      return;
    }

    this.filteredTreeNodeList = this.filterTreeNodes(
      this.treeNodeKeyAccountList,
      value,
    );

    this.updateTreeSelection();
  }

  // Helper method to recursively filter tree nodes based on label, parent label, and type
  private filterTreeNodes(nodes: any[], filterValue: string): any[] {
    const filtered: any[] = [];

    for (const node of nodes) {
      const label = node.label?.toLowerCase() ?? "";
      const parentLabel = node.data?.parentLabel?.toLowerCase() ?? "";
      const type = node.data?.type?.toLowerCase() ?? "";

      const nodeMatches =
        label.includes(filterValue) ||
        parentLabel.includes(filterValue) ||
        type.includes(filterValue);

      let filteredChildren: any[] = [];

      if (node.children?.length) {
        filteredChildren = this.filterTreeNodes(node.children, filterValue);
      }

      if (nodeMatches || filteredChildren.length) {
        filtered.push({
          ...node,
          children: filteredChildren.length ? filteredChildren : node.children,
          expanded: !!filteredChildren.length || nodeMatches,
        });
      }
    }

    return filtered;
  }

  // Remove deepClone completely if not strictly required
  // If needed, keep it but it's expensive for large trees
  private updateTreeSelection(): void {
    if (!this.selectedNodeKeyAccount?.length) return;

    const keySet = new Set(this.selectedNodeKeyAccount.map((node) => node.key));

    const updatedSelection = this.findNodesByKeys(
      this.filteredTreeNodeList,
      keySet,
    );

    Promise.resolve().then(() => {
      this.selectedNodeKeyAccount = this.segregateNodes(updatedSelection);
      this.cdr.detectChanges();
    });
  }

  // Helper method to find nodes by keys using a Set for O(1) lookups,
  // traversing the tree iteratively to avoid deep recursion
  private findNodesByKeys(nodes: any[], keySet: Set<string>): any[] {
    const found: any[] = [];

    const stack = [...nodes];

    while (stack.length) {
      const node = stack.pop();

      if (keySet.has(node.key)) {
        found.push(node);
      }

      if (node.children?.length) {
        stack.push(...node.children);
      }
    }

    return found;
  }

  // Method to handle selection change in the tree select component
  // and emit the selected key accounts to the parent component
  onSelectionChange(selectedKeyAccount: any[] = []): void {
    if (this.disabled) return;

    this.selectedNodeKeyAccount = this.segregateNodes(
      this.disableCheckboxSelection ? [selectedKeyAccount] : selectedKeyAccount,
    );
    this.onSelectedKeyAccountsEmit.emit(this.selectedNodeKeyAccount);
  }

  // Method to handle select all change event and emit the selected key accounts to the parent component
  onSelectAllChange(event: any): void {
    if (this.disabled) return;
    const allNodes = event?.checked
      ? this.getAllNodes(this.treeNodeKeyAccountList)
      : [];
    this.selectedNodeKeyAccount = this.segregateNodes(allNodes);

    this.onSelectedKeyAccountsEmit.emit(this.selectedNodeKeyAccount);
  }

  // Helper method to segregate key accounts and parent nodes (zone/country) based on their type
  private segregateNodes(nodes: any[] = []): any[] {
    const keyAccounts: any[] = [];
    const parents: any[] = [];

    for (const node of nodes) {
      const type = node?.data?.type;

      if (type === "zone" || type === "country") {
        parents.push(node);
      } else if (type === "keyAccount" || node?.id) {
        keyAccounts.push(node);
      }
    }

    return [...keyAccounts, ...parents];
  }

  // Helper method to get all nodes from the tree structure
  private getAllNodes(nodes: any[]): any[] {
    let allNodes: any[] = [];
    nodes.forEach((node) => {
      allNodes.push(node);
      if (node.children && node.children.length > 0) {
        allNodes = allNodes.concat(this.getAllNodes(node.children));
      }
    });
    return allNodes;
  }

  // Method to close the tree overlay
  // btnFlag: true = Apply button clicked, false = Cancel button clicked
  closeTreeOverlay(btnFlag: boolean = false) {
    if (!btnFlag) {
      // Cancel clicked - reset selections
      this.selectedNodeKeyAccount = [];
      this.selectAll = false;
    } else {
      // Apply clicked - emit only the segregated key accounts
      this.onSelectedKeyAccountsEmit.emit(
        this.segregateNodes(this.selectedNodeKeyAccount),
      );
    }

    // Close the overlay using ViewChild reference
    if (this.treeSelect) {
      this.treeSelect.hide();
    }
  }

  // Getter to check if any key accounts are selected
  get hasSelectedKeyAccounts(): boolean {
    return this.segregateNodes(this.selectedNodeKeyAccount).length > 0;
  }

  // Getter to return only leaf (grandchild / keyAccount) nodes for chip display
  get selectedKeyAccountLeaves(): any[] {
    return (this.selectedNodeKeyAccount ?? []).filter(
      (node) =>
        node?.data?.type === "keyAccount" ||
        (!node?.children?.length && node?.id),
    );
  }
}
